import type { TenantMemberRole, TenantTeamRow } from '@console/types'
import { useState } from 'react'
import { ApiError } from '../api.js'
import page from '../customer/CustomerPage.module.css'
import { SurfaceEmpty, SurfaceError, SurfaceLoading } from '../customer/SurfaceState.js'
import {
  useCreateTenantInvite,
  useRemoveTenantMember,
  useRevokeTenantInvite,
  useTenantTeam,
} from '../tenant-team/queries.js'
import { Button, Input, Modal, Select, Stamp, Table, Toast } from '../ui/index.js'
import styles from './CustomerTeamPage.module.css'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * `LGH-040` — customer console: team, invitations, roles.
 *
 * `R-091`: a role is chosen by the admin AT INVITE TIME, never self-selected
 * by the invitee — the picker below is the one and only place a role gets
 * assigned. `email` targets are refused server-side (`422`) — no outbound
 * email capability exists anywhere in this estate yet, same gap
 * `RfqSourceInvite` already named — so the form only offers phone.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * `DES-001`, presentation only — every mutation, its payload and its
 * invalidation are untouched. What changed, and why:
 *
 * - **The roster is a `<table>` now.** It was a `<ul>` where each `<li>` was
 *   its own five-column grid, so every row sized its own columns: measured at
 *   1024px, the phone column started at x=351, 351, 348 and 368 down four
 *   rows, and the status column at 765, 765, 756 and 815. A roster whose
 *   columns wobble row to row. One `<table>` fixes that by construction, and
 *   brings the column headers the list never had — "Admin" and "Active" sat in
 *   unlabelled columns.
 * - **Raw `<input>`/`<select>`/`<button>` replaced with the primitives.** The
 *   sibling price-lists screen already used `Input`; this one didn't, so two
 *   screens of the same console spoke two different form languages.
 * - **A successful invite said nothing.** The field cleared and a row appeared
 *   at the bottom of a list. Commercial rules says "Saved."; price lists
 *   reports its row counts; this said nothing at all.
 * - **The role picker kept its last value while the phone cleared**, so the
 *   next invite silently defaulted to whatever elevated role was used last —
 *   directly against this file's own "least privilege first" note. One line.
 * - **`Remove` and `Revoke` are `danger` now.** The two destructive controls
 *   on the screen looked exactly like everything else.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * `REQ-design-the-customer-console-ux-audit` finding T3, now built — 18UX's
 * own recommendation taken: `ui/Modal` for both, replacing `window.confirm()`
 * on Remove and Revoke's previous no-confirmation immediate fire. A raw
 * browser `confirm()` was the one piece of unstyled OS chrome left on this
 * screen; `Modal` gets focus-trapping and Escape-to-close for free, and now
 * both destructive actions share one safety model instead of two.
 * ═══════════════════════════════════════════════════════════════════════════
 */

const ROLE_OPTIONS: { value: TenantMemberRole; label: string }[] = [
  { value: 'SALES', label: 'Sales' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'ADMIN', label: 'Admin' },
]

const ROLE_LABEL: Record<TenantMemberRole, string> = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  SALES: 'Sales',
}

/**
 * What each role actually gets. `R-091` makes this the admin's choice, taken
 * once, for somebody else — and the picker offered three words with no
 * explanation of what any of them meant.
 *
 * Written from what this console itself enforces and nothing more: all three
 * `/customer/*` surfaces answer `403` to a non-admin (`TST-001`'s own refusal
 * tests), and approval authority is `PRC-003`'s. If the platform's own role
 * definitions say more than this, these three lines should be replaced by
 * whatever `1C` serves rather than restated here — noted in the REQ.
 */
const ROLE_HINT: Record<TenantMemberRole, string> = {
  SALES: 'Can work on quotes. Cannot change price lists, commercial rules or the team.',
  MANAGER: 'Can work on quotes and approve them. Cannot change settings or the team.',
  ADMIN: 'Full access, including price lists, commercial rules and this team.',
}

const INVITE_STATUS_LABEL: Record<'pending' | 'revoked', string> = {
  pending: 'Pending',
  revoked: 'Revoked',
}

function InviteForm() {
  const [phone, setPhone] = useState('')
  // Least privilege first — `R-091` names this as a rule, not a convenience.
  const [role, setRole] = useState<TenantMemberRole>('SALES')
  const [lastInvited, setLastInvited] = useState<string | null>(null)
  const invite = useCreateTenantInvite()

  return (
    <form
      className={page['card']}
      onSubmit={(event) => {
        event.preventDefault()
        const value = phone.trim()
        invite.mutate(
          { target: { kind: 'phone', value }, role },
          {
            onSuccess: () => {
              setPhone('')
              // See the header note: this stayed on its last value.
              setRole('SALES')
              setLastInvited(value)
            },
          },
        )
      }}
    >
      <h2 className={styles['cardTitle']}>Invite a team member</h2>
      <div className={page['formRow']}>
        <Input
          label="Mobile number"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          hint="They get the invite on WhatsApp."
          placeholder="+971 50 123 4567"
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value)
          }}
        />
        <Select
          label="Role"
          value={role}
          options={ROLE_OPTIONS}
          onChange={(event) => {
            setRole(event.target.value as TenantMemberRole)
          }}
        />
        <div className={page['submit']}>
          <Button
            type="submit"
            variant="primary"
            disabled={phone.trim() === ''}
            loading={invite.isPending}
          >
            {invite.isPending ? 'Sending…' : 'Send invite'}
          </Button>
        </div>
      </div>

      <p className={page['hint']}>{ROLE_HINT[role]}</p>

      {invite.isError && (
        <p role="alert" className={page['error']}>
          {invite.error instanceof ApiError
            ? invite.error.message
            : 'That invite could not be sent.'}
        </p>
      )}

      {invite.isSuccess && lastInvited !== null && (
        <Toast variant="ok">
          Invite sent to {lastInvited} on WhatsApp. It shows below as Pending until they join.
        </Toast>
      )}
    </form>
  )
}

/** Shared shape for a destructive row action gated by `Modal` — `T3`. */
function DestructiveRowAction({
  buttonLabel,
  pendingLabel,
  modalTitle,
  modalBody,
  confirmLabel,
  pending,
  onConfirm,
}: {
  buttonLabel: string
  pendingLabel: string
  modalTitle: string
  modalBody: string
  confirmLabel: string
  pending: boolean
  onConfirm: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  return (
    <>
      <Button
        type="button"
        variant="danger"
        className={page['rowAction']}
        loading={pending}
        onClick={() => {
          setConfirming(true)
        }}
      >
        {pending ? pendingLabel : buttonLabel}
      </Button>
      <Modal
        open={confirming}
        title={modalTitle}
        onClose={() => {
          setConfirming(false)
        }}
      >
        <p className={page['hint']}>{modalBody}</p>
        <div className={page['actions']}>
          <Button
            type="button"
            onClick={() => {
              setConfirming(false)
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            loading={pending}
            onClick={() => {
              setConfirming(false)
              onConfirm()
            }}
          >
            {confirmLabel}
          </Button>
        </div>
      </Modal>
    </>
  )
}

function MemberActions({ member }: { member: Extract<TenantTeamRow, { kind: 'member' }> }) {
  const remove = useRemoveTenantMember()
  return (
    <DestructiveRowAction
      buttonLabel="Remove"
      pendingLabel="Removing…"
      modalTitle="Remove from the team?"
      modalBody={`${member.name} will lose access to this account immediately. This can't be undone.`}
      confirmLabel="Remove"
      pending={remove.isPending}
      onConfirm={() => {
        remove.mutate(member.userId)
      }}
    />
  )
}

function InviteActions({ invite }: { invite: Extract<TenantTeamRow, { kind: 'invite' }> }) {
  const revoke = useRevokeTenantInvite()
  if (invite.status !== 'pending') return null
  return (
    <DestructiveRowAction
      buttonLabel="Revoke"
      pendingLabel="Revoking…"
      modalTitle="Revoke this invite?"
      modalBody={`The invite to ${invite.target.value} will stop working immediately. This can't be undone.`}
      confirmLabel="Revoke"
      pending={revoke.isPending}
      onConfirm={() => {
        // `TST-001` finding 2 — the roster's own invite rows key on
        // `inviteId`, not `id`; there is no `id` field on this shape.
        revoke.mutate(invite.inviteId)
      }}
    />
  )
}

/** The stamp per row state — `open` for an invite nobody has answered, `stop` for one withdrawn. */
function StatusCell({ row }: { row: TenantTeamRow }) {
  if (row.kind === 'member') {
    return (
      <span className={styles['status']}>
        <Stamp variant="ok" label="On the team" /> Active
      </span>
    )
  }
  return (
    <span className={styles['status']}>
      <Stamp
        variant={row.status === 'pending' ? 'open' : 'stop'}
        label={row.status === 'pending' ? 'Invited, not yet joined' : 'Invite withdrawn'}
      />{' '}
      {INVITE_STATUS_LABEL[row.status]}
    </span>
  )
}

export function CustomerTeamPage() {
  const team = useTenantTeam()

  // Members first, then invites still open, then withdrawn ones — a
  // just-created invite was landing underneath a revoked one.
  const rows: TenantTeamRow[] =
    team.data === undefined
      ? []
      : [
          ...team.data.filter((row) => row.kind === 'member'),
          ...team.data.filter((row) => row.kind === 'invite' && row.status === 'pending'),
          ...team.data.filter((row) => row.kind === 'invite' && row.status === 'revoked'),
        ]

  return (
    <main className={page['page']}>
      <h1 className={page['title']}>Team</h1>
      <p className={page['lede']}>
        Everyone here can sign in to this account. Price lists, commercial rules and this page are
        admin-only.
      </p>

      <InviteForm />

      <h2 className={page['sectionTitle']}>Who&rsquo;s on the account</h2>

      {team.isPending && <SurfaceLoading rows={4} />}
      {team.isError && <SurfaceError>The team list could not be loaded.</SurfaceError>}

      {team.data !== undefined &&
        (rows.length === 0 ? (
          <SurfaceEmpty>
            Nobody has been added yet. Invite your first team member above.
          </SurfaceEmpty>
        ) : (
          <Table
            rows={rows}
            rowKey={(row) => row.userId ?? row.inviteId}
            columns={[
              {
                key: 'name',
                header: 'Name',
                render: (row) =>
                  row.kind === 'member' ? (
                    row.name
                  ) : (
                    <span className={page['muted']}>Not joined yet</span>
                  ),
              },
              { key: 'phone', header: 'Mobile', render: (row) => row.target.value },
              { key: 'role', header: 'Role', render: (row) => ROLE_LABEL[row.role] },
              {
                key: 'status',
                header: 'Status',
                render: (row) => <StatusCell row={row} />,
              },
              {
                key: 'action',
                header: '',
                render: (row) =>
                  row.kind === 'member' ? (
                    <MemberActions member={row} />
                  ) : (
                    <InviteActions invite={row} />
                  ),
              },
            ]}
          />
        ))}
    </main>
  )
}
